import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { saveAs } from 'file-saver'
import api from '../services/api';
import InternalNotesModal from '../components/InternalNotesModal';
import ShipmentModal from '../components/ShipmentModal';
import PackingGuideDrawer from '../components/PackingGuideDrawer';
import SideDrawer from '../components/SideDrawer';
import {
  ArrowLeftIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CubeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';

// Status color mapping (text colors for the select dropdown)
const STATUS_COLORS = {
  SUBMITTED: 'bg-blue-100 text-blue-800',
  INTERACTING: 'bg-purple-100 text-purple-800',
  DEAL: 'bg-green-100 text-green-800',
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-emerald-100 text-emerald-800',
  ONGOING: 'bg-indigo-100 text-indigo-800',
  INSPECTION: 'bg-orange-100 text-orange-800',
  READY: 'bg-teal-100 text-teal-800',
  DELIVERING: 'bg-cyan-100 text-cyan-800',
  DONE: 'bg-green-200 text-green-800',
  ON_HOLD: 'bg-red-100 text-red-800',
  // Fallback for any other status
  DEFAULT: 'bg-gray-100 text-gray-800'
};

// Safely resolve i18n value (handles string, {default, en}, or {pt, en_US, pt_BR})
const resolveI18n = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value.default || value.pt || value.en || value.en_US || Object.values(value)[0] || '';
  }
  return String(value);
};

const PACKING_GUIDE_STATUSES = ['ONGOING', 'INSPECTION', 'READY'];

// Convert status key to readable label
const formatStatusLabel = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

function SlicerQuoteDetail({ quoteId: propId, embedded = false }) {
  const params = useParams();
  const navigate = useNavigate();
  const id = propId || params.id;
  const [quote, setQuote] = useState(null);
  const [groupSize, setGroupSize] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [showPackingGuide, setShowPackingGuide] = useState(false);
  const [showLogDrawer, setShowLogDrawer] = useState(false);
  const [logContent, setLogContent] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  // const [expandedPlates, setExpandedPlates] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    costs: true,
    plates: false,
    addons: true,
    profitMargin: true,
    discounts: true,
    summary: true
  });

  const fetchAvailableStatuses = useCallback(async () => {
    try {
      const response = await api.get('/auth/slicer-quote/board');
      const data = response.data.data || response.data;
      setAvailableStatuses(data.availableStatuses || []);
    } catch (error) {
      console.error('Error fetching available statuses:', error);
      // If fetch fails, continue with empty array (won't show status select)
    }
  }, []);

  const fetchQuoteDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/auth/slicer-quote/${id}`);
      setQuote(response.data.data);
    } catch (error) {
      console.error('Error fetching quote detail:', error);
      if (!embedded) {
        alert('Failed to load quote details');
        navigate('/slicer-quotes');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- embedded is static prop
  }, [id, navigate]);

  useEffect(() => {
    fetchAvailableStatuses();
    fetchQuoteDetail();
  }, [fetchAvailableStatuses, fetchQuoteDetail]);

  useEffect(() => {
    if (!quote?.linkedOrderId) {
      setGroupSize(null);
      return;
    }
    let cancelled = false;
    api
      .get('/auth/slicer-quote/board-grouped', {
        params: { linkedOrderId: quote.linkedOrderId },
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data || {};
        const total = Object.values(data.groups || {}).reduce(
          (sum, g) => sum + (g.aggregate?.total || 0),
          0
        );
        setGroupSize(total);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [quote?.linkedOrderId]);

  const handleSaveNotes = async (notes) => {
    try {
      await api.patch(`/auth/slicer-quote/${id}`, {
        internalNotes: notes
      });
      setShowNotesModal(false);
      await fetchQuoteDetail();
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save internal notes');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'DELIVERING') {
      setShowShipmentModal(true);
      return;
    }
    try {
      await api.patch(`/auth/slicer-quote/${id}`, {
        status: newStatus
      });
      await fetchQuoteDetail();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update quote status');
    }
  };

  const confirmShipmentFromDetail = async ({ shipments, internalNotes }) => {
    try {
      await api.patch(`/auth/slicer-quote/${id}`, {
        status: 'DELIVERING',
        shipments,
        ...(internalNotes ? { internalNotes } : {}),
      });
      setShowShipmentModal(false);
      await fetchQuoteDetail();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update quote status');
    }
  };


  const handleDownloadJobFile = async (fileName) => {
    try {
      const url = `/auth/slicer-quote/download/${quote._id}/${fileName}`
      const response = await api.get(url, {
        responseType: 'blob'
      })

      saveAs(response.data, fileName)
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleViewLog = async () => {
    setShowLogDrawer(true);
    if (logContent !== null) return;

    try {
      setLogLoading(true);
      const url = `/auth/slicer-quote/download/${quote._id}/slicer.log`;
      const response = await api.get(url, { responseType: 'text' });
      setLogContent(response.data);
    } catch (error) {
      console.error('Error fetching slicer log:', error);
      setLogContent('Failed to load slicer log.');
    } finally {
      setLogLoading(false);
    }
  };

  const handleDownloadLog = () => {
    if (logContent) {
      const blob = new Blob([logContent], { type: 'text/plain' });
      saveAs(blob, 'slicer.log');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value, currency = 'EUR') => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const parseColor = (colorString) => {
    try {
      return JSON.parse(colorString);
    } catch {
      return null;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // const togglePlate = (plateIndex) => {
  //   setExpandedPlates(prev => ({
  //     ...prev,
  //     [plateIndex]: !prev[plateIndex]
  //   }));
  // };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!quote) {
    return <div className="p-4">Quote not found</div>;
  }

  const color = parseColor(quote.color);
  const parsedQuote = quote.parsedQuote;

  let selectedTimelineKey = null
  let selectedQuoteTimeline = null

  if (quote.timeline) {
    selectedTimelineKey = quote.timeline.key;
    selectedQuoteTimeline = parsedQuote.summary.timelineOptions.find(item => item.key === selectedTimelineKey)
  }



  return (
    <div className={embedded ? 'p-4' : 'p-5 max-w-7xl mx-auto'}>
      {/* Header */}
      <div className="mb-4">
        {!embedded && (
          <button
            onClick={() => navigate('/slicer-quotes')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Board
          </button>
        )}

        <PageHeader
          title="Quote Details"
          description={`Quote ID: ${quote._id}`}
          actions={
            <div className="flex items-center gap-3">
              {PACKING_GUIDE_STATUSES.includes(quote.status) && (
                <button
                  onClick={() => setShowPackingGuide(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <CubeIcon className="h-5 w-5" />
                  Packing Guide
                </button>
              )}
              <select
                value={quote.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className={`px-4 py-2 rounded-lg font-medium border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${STATUS_COLORS[quote.status] || STATUS_COLORS.DEFAULT}`}
              >
                {availableStatuses.map((statusKey) => (
                  <option key={statusKey} value={statusKey}>
                    {formatStatusLabel(statusKey)}
                  </option>
                ))}
              </select>
              <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium">
                Step {quote.step}
              </span>
            </div>
          }
        />
      </div>

      {/* Main Content Grid */}
      <div className={`grid gap-4 ${embedded ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
        {/* Left Column - Contact & Timeline */}
        <div className={`${embedded ? '' : 'lg:col-span-1'} space-y-4`}>
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-gray-500" />
              Contact Information
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-600">Name</label>
                <p className="font-medium">{quote.contact?.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 flex items-center gap-1">
                  <EnvelopeIcon className="h-4 w-4" />
                  Email
                </label>
                <p className="font-medium">{quote.contact?.email}</p>
              </div>
              {quote.contact?.phone && (
                <div>
                  <label className="text-sm text-gray-600 flex items-center gap-1">
                    <PhoneIcon className="h-4 w-4" />
                    Phone
                  </label>
                  <p className="font-medium">{quote.contact.phone}</p>
                </div>
              )}
              {quote.contact?.company && (
                <div>
                  <label className="text-sm text-gray-600 flex items-center gap-1">
                    <BuildingOfficeIcon className="h-4 w-4" />
                    Company
                  </label>
                  <p className="font-medium">{quote.contact.company}</p>
                </div>
              )}
              {quote.linkedOrderId && (
                <div>
                  <label className="text-sm text-gray-600 flex items-center gap-1">
                    <TruckIcon className="h-4 w-4" />
                    Group
                  </label>
                  <Link
                    to={`/slicer-quotes?linkedOrderId=${encodeURIComponent(quote.linkedOrderId)}`}
                    className="inline-flex items-center gap-2 mt-1 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-medium"
                  >
                    {quote.linkedOrderNumber || 'Linked order'}
                    {groupSize !== null && (
                      <span className="text-xs text-indigo-500">· {groupSize} quotes</span>
                    )}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* File Information */}
          {quote.slicerJob?.file && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-gray-500" />
                File
              </h2>
              <div className="space-y-2">
                <p className="font-medium text-gray-900 break-all">{quote.slicerJob.file.name}</p>
                <div className="flex gap-4 text-xs text-gray-500">
                  {quote.slicerJob.file.size && (
                    <span>{(quote.slicerJob.file.size / 1024 / 1024).toFixed(2)} MB</span>
                  )}
                  {quote.slicerJob.file.mimeType && (
                    <span>{quote.slicerJob.file.mimeType}</span>
                  )}
                </div>
                {quote.slicerJob.is3MF && (
                  <span className="inline-block mt-1 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">3MF</span>
                )}
              </div>
            </div>
          )}

          {/* Total Quote Amount */}
          {selectedQuoteTimeline && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow-sm border-2 border-green-200 p-4">
              <h2 className="text-base font-semibold mb-2 text-gray-900">Total Quote</h2>
              {quote.priceSnapshot ? (
                <>
                  <div className="text-2xl font-bold text-green-600">
                    {(quote.priceSnapshot.totalPriceInCents / 100).toFixed(2)} {quote.priceSnapshot.currency || 'EUR'}
                  </div>
                  {quote.priceSnapshot.unitPriceInCents && (
                    <div className="text-sm text-gray-600 mt-1">
                      Unit price: {(quote.priceSnapshot.unitPriceInCents / 100).toFixed(2)} {quote.priceSnapshot.currency || 'EUR'}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(quote.quoteTotal)} <small className="text-sm">(no vat)</small>
                  </div>
                  <div className="text-base font-bold mt-2">
                    {formatCurrency(selectedQuoteTimeline.total)}
                  </div>
                </>
              )}



            </div>
          )}

          {/* Timeline */}
          {selectedQuoteTimeline && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gray-500" />
                Selected Timeline
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-600">Timeline</label>
                  <p className="font-medium">{resolveI18n(selectedQuoteTimeline.name)}</p>
                </div>
                {selectedQuoteTimeline.actualReadyDays > 0 && (
                  <div>
                    <label className="text-sm text-gray-600">Ready ETA</label>
                    <p className="font-medium">{selectedQuoteTimeline.actualReadyDays} days</p>
                  </div>
                )}
                {selectedQuoteTimeline.feePercentage > 0 && (
                  <div>
                    <label className="text-sm text-gray-600">Rush Fee</label>
                    <p className="font-medium">
                      {selectedQuoteTimeline.feePercentage}% (min {formatCurrency(selectedQuoteTimeline.minimumFee)})
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quote Metadata */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold mb-4">Quote Metadata</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Quote ID</span>
                <span className="font-mono text-xs">{quote._id}</span>
              </div>
              {/* <div className="flex justify-between">
                <span className="text-gray-600">Session ID</span>
                <span className="font-mono text-xs">{quote.sessionId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Request IP</span>
                <span className="font-medium">{quote.reqIp}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active</span>
                <span className={`font-medium ${quote.isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {quote.isActive ? 'Yes' : 'No'}
                </span>
              </div> */}
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="font-medium">{formatDate(quote.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Updated</span>
                <span className="font-medium">{formatDate(quote.updatedAt)}</span>
              </div>

            </div>
          </div>

          {/* Internal Notes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Internal Notes</h2>
              <button
                onClick={() => setShowNotesModal(true)}
                className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
              >
                <PencilIcon className="h-4 w-4" />
                Edit
              </button>
            </div>
            {quote.internalNotes ? (
              <p className="text-gray-700 whitespace-pre-wrap">{quote.internalNotes}</p>
            ) : (
              <p className="text-gray-400 italic">No internal notes</p>
            )}
          </div>
        </div>

        {/* Right Column - Technical Details & Pricing */}
        <div className={`${embedded ? '' : 'lg:col-span-2'} space-y-4`}>
          {/* Print Configuration */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CubeIcon className="h-5 w-5 text-gray-500" />
              Print Configuration
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {quote.material && (
                <div className="col-span-2">
                  <label className="text-sm text-gray-600">Material</label>
                  <p className="font-medium text-base">{quote.material.name}</p>
                </div>
              )}
              <div>
                <label className="text-sm text-gray-600">Quantity</label>
                <p className="font-medium text-base">{quote.quantity} units</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Support</label>
                <p className={`font-medium text-base ${quote.support ? 'text-green-600' : 'text-gray-600'}`}>
                  {quote.support ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Infill Density</label>
                <p className="font-medium">{quote.infill}%</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Wall Loops</label>
                <p className="font-medium">{quote.walls} layers</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Top Layers</label>
                <p className="font-medium">{quote.topLayers}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Bottom Layers</label>
                <p className="font-medium">{quote.bottomLayers}</p>
              </div>
              {color && (
                <div className="col-span-2">
                  <label className="text-sm text-gray-600">Selected Color</label>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-8 h-8 rounded border-2 border-gray-300 shadow-sm"
                      style={{ backgroundColor: `#${color.hex}` }}
                    />
                    <div>
                      <p className="font-medium">{color.name}</p>
                      <p className="text-xs text-gray-500">#{color.hex}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {quote.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="text-sm text-gray-600">Customer Description</label>
                <p className="font-medium mt-1 text-gray-900">{quote.description}</p>
              </div>
            )}
          </div>

          {/* Material Extra */}
          {quote.extra && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4">Material Extra/Finish</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Extra Name</label>
                  <p className="font-medium text-lg">{resolveI18n(quote.extra.name)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Cost Multiplier</label>
                  <p className="font-medium text-base text-orange-600">{quote.extra.materialCostMultiplier}x</p>
                </div>

              </div>
            </div>
          )}

          {/* Quality/Process Profile */}
          {quote.quality && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4">Quality/Process Profile</h2>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Profile Name</label>
                    <p className="font-medium text-lg">{resolveI18n(quote.quality.name)}</p>
                  </div>
                  {quote.quality.quality?.nozzle && (
                    <div>
                      <label className="text-sm text-gray-600">Nozzle Size</label>
                      <p className="font-medium">{quote.quality.quality.nozzle} mm</p>
                    </div>
                  )}
                  {quote.quality.quality?.layerHeight && (
                    <div>
                      <label className="text-sm text-gray-600">Layer Height</label>
                      <p className="font-medium">{quote.quality.quality.layerHeight} mm</p>
                    </div>
                  )}
                  {quote.quality.quality?.multiplier && (
                    <div>
                      <label className="text-sm text-gray-600">Cost Multiplier</label>
                      <p className="font-medium">{quote.quality.quality.multiplier}x</p>
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}

          {/* Extra Services Section (Standalone for visibility) */}
          {parsedQuote?.breakdown && (parsedQuote.breakdown.appliedServices?.length > 0 || parsedQuote.breakdown.offerServices?.length > 0) && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4">Extra Services</h2>

              {/* Applied Services */}
              {parsedQuote.breakdown.appliedServices && parsedQuote.breakdown.appliedServices.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                    Applied Services (Charged)
                  </div>
                  <div className="space-y-2">
                    {parsedQuote.breakdown.appliedServices.map((service, index) => (
                      <div key={index} className={`p-3 rounded ${service.isPromotional ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' : 'bg-gray-50'
                        }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900">
                                {resolveI18n(service.name)}
                              </div>
                              {service.isPromotional && (
                                <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-medium rounded-full">
                                  PROMO
                                </span>
                              )}
                            </div>
                            {service.description && (
                              <div className="text-xs text-gray-600 mt-1">
                                {resolveI18n(service.description)}
                              </div>
                            )}
                            {service.promotionalDescription && (
                              <div className="text-xs text-green-700 mt-1 font-medium">
                                {resolveI18n(service.promotionalDescription)}
                              </div>
                            )}
                          </div>
                          <div className="ml-4 text-right">
                            {service.isPromotional && service.regularPrice && service.regularPrice > service.price ? (
                              <div>
                                <div className="text-sm text-gray-500 line-through">
                                  {formatCurrency(service.regularPrice)}
                                </div>
                                <div className="font-medium text-green-600">
                                  {service.price === 0 ? 'FREE' : formatCurrency(service.price)}
                                </div>
                              </div>
                            ) : (
                              <span className="font-medium text-gray-900">
                                {service.price === 0 ? 'FREE' : formatCurrency(service.price)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-gray-200 mt-3">
                      <span className="text-sm font-semibold">Total Charged Services</span>
                      <span className="font-semibold text-green-600">
                        {formatCurrency(parsedQuote.breakdown.appliedServices.reduce((sum, service) => sum + (service.price || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Offer Services */}
              {parsedQuote.breakdown.offerServices && parsedQuote.breakdown.offerServices.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">
                    Offer Services (Free/Included)
                  </div>
                  <div className="space-y-2">
                    {parsedQuote.breakdown.offerServices.map((service, index) => (
                      <div key={index} className="p-3 rounded bg-blue-50 border border-blue-200">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900">
                                {resolveI18n(service.name)}
                              </div>
                              <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full">
                                OFFER
                              </span>
                            </div>
                            {service.description && (
                              <div className="text-xs text-gray-600 mt-1">
                                {resolveI18n(service.description)}
                              </div>
                            )}
                            {service.promotionalDescription && (
                              <div className="text-xs text-blue-700 mt-1 font-medium">
                                {resolveI18n(service.promotionalDescription)}
                              </div>
                            )}
                          </div>
                          <div className="ml-4 text-right">
                            <span className="font-medium text-blue-600">FREE</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Comprehensive Pricing Breakdown */}
          {parsedQuote && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CurrencyDollarIcon className="h-5 w-5 text-gray-500" />
                Complete Pricing Breakdown
              </h2>

              {parsedQuote.breakdown && (
                <div className="space-y-4">

                  {/* Step 1: Per-Unit Base Costs */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('costs')}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-semibold text-gray-900">Base Costs</span>
                      {expandedSections.costs ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.costs && parsedQuote.breakdown.plates && parsedQuote.breakdown.plates.length > 0 && (
                      <div className="p-4 space-y-3 border-t border-gray-200">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Material Cost</span>
                          <span className="font-medium">{formatCurrency(parsedQuote.breakdown.materialCost)}</span>
                        </div>

                        <div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Operational Cost</span>
                            <span className="font-medium">{formatCurrency(parsedQuote.breakdown.operationalCost)}</span>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Margin</span>
                            <span className="font-medium">{formatCurrency(parsedQuote.breakdown.totalMargin)}</span>
                          </div>
                        </div>

                        <div className="flex justify-between pt-2 border-t border-gray-200">
                          <span className="text-sm font-semibold text-gray-900">Subtotal</span>
                          <span className="font-semibold">
                            {formatCurrency(
                              parsedQuote.breakdown.subtotalBeforeExtras
                            )}
                          </span>
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Step 3: Extra Services & Rush Fees (if any) */}
                  {(parsedQuote.breakdown.extraServicesCost > 0 || parsedQuote.breakdown.rushFee > 0) && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 bg-yellow-50 space-y-3">
                        <div className="font-semibold text-gray-900">Add Extra Services & Rush Fees</div>

                        {parsedQuote.breakdown.extraServicesCost > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Extra Services</span>
                            <span className="font-medium text-yellow-700">
                              +{formatCurrency(parseFloat(parsedQuote.breakdown.extraServicesCost))}
                            </span>
                          </div>
                        )}

                        {parsedQuote.breakdown.rushFee > 0 && (
                          <div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Rush Fee</span>
                              <span className="font-medium text-orange-600">
                                +{formatCurrency(parseFloat(parsedQuote.breakdown.rushFee))}
                              </span>
                            </div>
                            {parsedQuote.breakdown.rushFeeBreakdown && (
                              <div className="ml-4 mt-1 text-xs text-gray-600">
                                {parsedQuote.breakdown.rushFeeBreakdown.percentage && (
                                  <div>Percentage: {(parsedQuote.breakdown.rushFeeBreakdown.percentage * 100).toFixed(2)}%</div>
                                )}
                                {parsedQuote.breakdown.rushFeeBreakdown.minimumFee && (
                                  <div>Minimum: {formatCurrency(parsedQuote.breakdown.rushFeeBreakdown.minimumFee)}</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex justify-between pt-2 border-t border-yellow-200">
                          <span className="text-sm font-semibold text-gray-900">Subtotal After Additions</span>
                          <span className="font-semibold text-yellow-900">
                            {formatCurrency(
                              parseFloat(parsedQuote.breakdown.materialCost) +
                              parseFloat(parsedQuote.breakdown.operationalCost) +
                              parseFloat(parsedQuote.breakdown.profitMargin?.profitAmount || 0) +
                              parseFloat(parsedQuote.breakdown.extraServicesCost || 0) +
                              parseFloat(parsedQuote.breakdown.rushFee || 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Bulk Discounts */}
                  {parsedQuote.breakdown.bulkDiscount && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 transition-colors"
                      >
                        <span className="font-semibold text-gray-900">Bulk Discount ({resolveI18n(parsedQuote.breakdown.bulkDiscount.tierName)})</span>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-green-600">
                            -{formatCurrency(parseFloat(parsedQuote.breakdown.bulkDiscount.amount))} ({parseInt(parsedQuote.breakdown.bulkDiscount.percentage * 100)}%)
                          </span>

                        </div>
                      </button>

                    </div>
                  )}







                  {/* Add-ons Breakdown */}
                  {parsedQuote?.breakdown && (parsedQuote.breakdown.appliedServices?.length > 0 || parsedQuote.breakdown.offerServices?.length > 0) && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('addons')}
                        className="w-full flex items-center justify-between p-4 bg-yellow-50 hover:bg-yellow-100 transition-colors"
                      >
                        <span className="font-semibold text-gray-900">
                          Extra Services ({(parsedQuote.breakdown.appliedServices?.length || 0) + (parsedQuote.breakdown.offerServices?.length || 0)})
                        </span>
                        {expandedSections.addons ? (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      {expandedSections.addons && (
                        <div className="p-4 space-y-3 border-t border-gray-200">
                          {/* Applied Services */}
                          {parsedQuote.breakdown.appliedServices && parsedQuote.breakdown.appliedServices.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                                Applied Services (Charged)
                              </div>
                              <div className="space-y-2">
                                {parsedQuote.breakdown.appliedServices.map((service, index) => (
                                  <div key={index} className={`p-3 rounded ${service.isPromotional ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200' : 'bg-gray-50'
                                    }`}>
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <div className="font-medium text-gray-900">
                                            {resolveI18n(service.name)}
                                          </div>
                                          {service.isPromotional && (
                                            <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-medium rounded-full">
                                              PROMO
                                            </span>
                                          )}
                                        </div>
                                        {service.description && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            {resolveI18n(service.description)}
                                          </div>
                                        )}
                                        {service.promotionalDescription && (
                                          <div className="text-xs text-green-700 mt-1 font-medium">
                                            {resolveI18n(service.promotionalDescription)}
                                          </div>
                                        )}
                                        {service.chargeType && (
                                          <div className="text-xs text-gray-500 mt-1">
                                            Charge Type: {service.chargeType}
                                          </div>
                                        )}
                                      </div>
                                      <div className="ml-4 text-right">
                                        {service.isPromotional && service.regularPrice && service.regularPrice > service.price ? (
                                          <div>
                                            <div className="text-sm text-gray-500 line-through">
                                              {formatCurrency(service.regularPrice)}
                                            </div>
                                            <div className="font-medium text-green-600">
                                              {service.price === 0 ? 'FREE' : formatCurrency(service.price)}
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="font-medium text-gray-900">
                                            {service.price === 0 ? 'FREE' : formatCurrency(service.price)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Offer Services */}
                          {parsedQuote.breakdown.offerServices && parsedQuote.breakdown.offerServices.length > 0 && (
                            <div className="pt-3">
                              <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                                Offer Services (Free/Included)
                              </div>
                              <div className="space-y-2">
                                {parsedQuote.breakdown.offerServices.map((service, index) => (
                                  <div key={index} className="p-3 rounded bg-blue-50 border border-blue-200">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <div className="font-medium text-gray-900">
                                            {resolveI18n(service.name)}
                                          </div>
                                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded-full">
                                            OFFER
                                          </span>
                                        </div>
                                        {service.description && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            {resolveI18n(service.description)}
                                          </div>
                                        )}
                                        {service.promotionalDescription && (
                                          <div className="text-xs text-blue-700 mt-1 font-medium">
                                            {resolveI18n(service.promotionalDescription)}
                                          </div>
                                        )}
                                        {service.chargeType && (
                                          <div className="text-xs text-gray-500 mt-1">
                                            Charge Type: {service.chargeType}
                                          </div>
                                        )}
                                      </div>
                                      <div className="ml-4 text-right">
                                        <span className="font-medium text-blue-600">
                                          FREE
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Total */}
                          {parsedQuote.breakdown.appliedServices && parsedQuote.breakdown.appliedServices.length > 0 && (
                            <div className="flex justify-between pt-2 border-t border-gray-200">
                              <span className="text-sm font-semibold">Total Extra Services (Charged)</span>
                              <span className="font-semibold">
                                {formatCurrency(parsedQuote.breakdown.appliedServices.reduce((sum, service) => sum + (service.price || 0), 0))}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 5: Final Totals with VAT */}
                  {parsedQuote.summary && (
                    <div className="border-2 border-indigo-300 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleSection('summary')}
                        className="w-full flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                      >
                        <span className="font-semibold text-gray-900">Final Totals</span>
                        {expandedSections.summary ? (
                          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      {expandedSections.summary && (
                        <div className="p-4 space-y-3 border-t border-gray-200">
                          {selectedQuoteTimeline && selectedQuoteTimeline.totalBeforeVat && (
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Total (Before VAT)</span>
                              <span className="font-medium">
                                {formatCurrency(parseFloat(selectedQuoteTimeline.totalBeforeVat))}
                              </span>
                            </div>
                          )}
                          {parsedQuote.summary.vat && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  VAT
                                </span>
                                <span className="font-medium text-indigo-600">
                                  {(parsedQuote.summary.vat.rate * 100).toFixed(0)}%
                                </span>
                              </div>

                            </>
                          )}
                          {selectedQuoteTimeline && (
                            <div className="flex justify-between pt-3 border-t-2 border-indigo-200">
                              <span className="text-lg font-bold text-gray-900">TOTAL QUOTE</span>
                              <span className="text-lg font-bold text-indigo-600">
                                {formatCurrency(selectedQuoteTimeline.total, parsedQuote.currency?.code)}
                              </span>
                            </div>
                          )}

                          {/* Technical Details */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="text-xs font-medium text-gray-700 mb-2">Technical Details:</div>
                            {parsedQuote.metadata.totalETA && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Total Print Time</span>
                                <span className="font-medium">
                                  {Math.floor(parsedQuote.metadata.totalETA / 3600)}h{' '}
                                  {Math.floor((parsedQuote.metadata.totalETA % 3600) / 60)}m
                                </span>
                              </div>
                            )}
                            {parsedQuote.metadata.totalMaterialWeight && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Total Material Used</span>
                                <span className="font-medium">
                                  {parsedQuote.metadata.totalMaterialWeight}g ({(parseFloat(parsedQuote.metadata.totalMaterialWeight) / 1000).toFixed(3)}kg)
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Slicer Job Link */}
          {quote.slicerJob && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Slicer Job</h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${quote.slicerJob.status === 'COMPLETED' || quote.slicerJob.status === 'SETTLED' ? 'bg-green-100 text-green-800' :
                  quote.slicerJob.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                    quote.slicerJob.status === 'IN_PROGRESS' || quote.slicerJob.status === 'SENT' ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-800'
                  }`}>
                  {quote.slicerJob.status}
                </span>
              </div>
              <button
                onClick={() => navigate(`/slicer-jobs/${quote.slicerJob._id}`)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                View Slicer Job Details
              </button>
            </div>
          )}

          {/* Output Files */}
          {quote.slicerJob?.jobFiles && quote.slicerJob.jobFiles.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DocumentArrowDownIcon className="h-5 w-5 text-gray-500" />
                Output Files ({quote.slicerJob.jobFiles.length})
              </h2>
              <div className="space-y-2">
                {quote.slicerJob.jobFiles.map((file, index) => {
                  const isSlicerLog = file.fileName === 'slicer.log';

                  if (isSlicerLog) {
                    return (
                      <button
                        key={index}
                        onClick={handleViewLog}
                        className="w-full flex items-center justify-between p-3 bg-amber-50 hover:bg-amber-100 rounded transition-colors cursor-pointer"
                      >
                        <div className="flex flex-col items-start">
                          <span className="font-medium text-amber-900">{file.fileName}</span>
                          <span className="text-xs text-amber-700 mt-1">{file.s3Key}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-5 w-5 text-amber-600" />
                          <span className="text-xs text-amber-700">View</span>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleDownloadJobFile(file.fileName)}
                      className="w-full flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 rounded transition-colors cursor-pointer"
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-green-900">{file.fileName}</span>
                        <span className="text-xs text-green-700 mt-1">{file.s3Key}</span>
                      </div>
                      <DocumentArrowDownIcon className="h-5 w-5 text-green-600" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Shipment Modal (DELIVERING transition) */}
      {showShipmentModal && (
        <ShipmentModal
          quote={quote}
          onConfirm={confirmShipmentFromDetail}
          onCancel={() => setShowShipmentModal(false)}
        />
      )}

      {/* Internal Notes Modal */}
      {showNotesModal && (
        <InternalNotesModal
          quote={quote}
          onSave={handleSaveNotes}
          onCancel={() => setShowNotesModal(false)}
        />
      )}

      {/* Slicer Log Drawer */}
      {quote.slicerJob?.jobFiles?.some(f => f.fileName === 'slicer.log') && (
        <SideDrawer
          open={showLogDrawer}
          onClose={() => setShowLogDrawer(false)}
          title="Slicer Log"
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-600">slicer.log</span>
              <button
                onClick={handleDownloadLog}
                disabled={!logContent || logLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </button>
            </div>
            {logLoading ? (
              <div className="text-center py-12 text-gray-500">Loading log content...</div>
            ) : (
              <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg text-xs font-mono overflow-auto max-h-[calc(100vh-180px)] whitespace-pre-wrap break-words">
                {logContent || 'No log content available.'}
              </pre>
            )}
          </div>
        </SideDrawer>
      )}

      {/* Packing Guide Drawer */}
      <PackingGuideDrawer
        open={showPackingGuide}
        onClose={() => setShowPackingGuide(false)}
        quoteId={id}
      />
    </div>
  );
}

export default SlicerQuoteDetail;
